import React, { useState, useEffect, useContext } from 'react'
import SVG from 'react-inlinesvg'
import DayPicker from 'react-day-picker'
import styles from './date-picker-styles.module.css'
import {
  getPlanAheadPricing,
  getPlanAheadFromEvent,
  attendanceInfo
} from '../helpers/calendar'
import params from '../../services/params'
import storage from '../../services/storage'
import { DateTime } from 'luxon'
import { Contact, Loading } from '../../shared/components'
import { CalendarCaption } from '../../shared/components/calendar-caption'
import DateFormat from '../../shared/helpers/date-format'
import high from '../../../images/busy-icon.svg'
import medium from '../../../images/less-busy-icon.svg'
import ListView from '../views/list-view'
import ListViewFromEvent from '../views/list-view-from-event'
import PriceTable from './price-table'
import { TicketsAppContext } from '../context/tickets-app-context'

const { dashed, fullDay, monthYear } = DateFormat

// In order to support system specs that need to present a consistent date on
// both the front- and back-end of the application, we need some external way
// of setting the current date.  This should be parseable date string set to
// what we want the 'current date' to be.
// This is why some rspecs use the method with_current_date_as
const TEST_CURRENT_DATE = storage.getItem('testCurrentDate')
let TEST_JS_DATE = null
if (TEST_CURRENT_DATE) {
  const [year, month, day] = TEST_CURRENT_DATE.split('-')
  TEST_JS_DATE = new Date(year, month - 1, day)
}

const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MODIFIERS = {
  disabled: {
    before: TEST_JS_DATE ? TEST_JS_DATE : new Date()
  }
}
const todayDateTime = new DateTime(new Date(), { zone: 'America/Chicago' })

function Calendar({
  selectDay,
  minimumAvailability,
  selectedDate,
  isMobiDisplayed,
  setIsMobiDisplayed,
  priceTableInfo,
  setPriceTableInfo,
  hours,
  showModal,
  remainingTicketsLoading
}) {
  const { chosenEvent } = useContext(TicketsAppContext)
  const [loading, setLoading] = useState(true)
  const [disabledDates, setDisabledDates] = useState(MODIFIERS.disabled)
  const [noDatesAvailable, setNoDatesAvailable] = useState(false)
  // if running rspecs or in Circle CI the browser needs to know what date time the server is using
  const [calendarStartDate, setCalendarStartDate] = useState(
    TEST_JS_DATE ? DateTime.fromJSDate(TEST_JS_DATE) : todayDateTime
  )
  const [selectedDay, setSelectedDay] = useState(new Date())
  // lastSelectedDay is used to remember what day is no longer selected for stlying and aria label purposes
  const [lastSelectedDay, setLastSelectedDay] = useState(null)
  const [activeMonth, setActiveMonth] = useState(null)
  const [planningInfo, setPlanningInfo] = useState([])
  const [error, setError] = useState(null)

  let [fromEvent, queryDate] = params.getAll('event_id', 'date')
  const currentDate = TEST_JS_DATE ? TEST_JS_DATE : new Date()
  const pickedDay = selectedDay ? new Date(selectedDay) : new Date(selectedDate)
  let initialMonth = calendarStartDate.toJSDate()
  let storageDateTime = storage.getItem('day')
    ? DateTime.fromISO(storage.getItem('day')).setZone('America/Chicago')
    : null

  // put event listeners on the window to only make the calls needed for mobile if the view is small enough
  const mediaMatcher = mediaQuery => {
    try {
      // Chrome & Firefox
      mediaQuery.addEventListener('change', e => {
        setIsMobiDisplayed(e.matches)
      })
    } catch (_err) {
      try {
        // Safari
        mediaQuery.addListener(e => {
          setIsMobiDisplayed(e.matches)
        })
      } catch (err2) {
        console.error(err2)
      }
    }
  }

  const toLuxonDate = date => {
    if (!date.isLuxonDateTime) {
      date = DateTime.fromISO(dashed(date))
    }
    return date
  }

  const onDayClick = (day, _, e) => {
    // confirm this class is still used
    if (e.currentTarget.classList.contains('disabled')) {
      e.stopPropagation()
      return false
    }
    const selectedDate = new Date(day)
    const currentDate = TEST_JS_DATE ? TEST_JS_DATE : new Date()
    const oldDay = lastSelectedDay
    // return if trying to pick a date in the past
    if (
      selectedDate.getDate() != currentDate.getDate() &&
      selectedDate < currentDate
    ) {
      return
    }

    // there is an issue with react-day-picker not changing aria-labels
    // temporary fix
    if (oldDay) {
      oldDay.setAttribute('aria-selected', false)
    }
    e.currentTarget.setAttribute('aria-selected', true)

    const selectedDateString = selectedDate.toISOString()
    setSelectedDay(selectedDateString)
    setLastSelectedDay(e.currentTarget)
    selectDay(selectedDateString)
    setPriceTableInfo(
      planningInfo.find(day => day['date'] === dashed(storage.getItem('day')))
    )
  }

  const onMonthChange = async month => {
    setLoading(true)
    let today = new Date()
    const monthStartDate = month < today ? today : month
    // active Month is used in month announcement
    setActiveMonth(toLuxonDate(monthStartDate))

    if (fromEvent) {
      // noDatesAvailable is not set here to allow users to continue paging through months for results
      // using getPlanAheadFromEvent instead of getPlanAheadPricing because the user is trying to access a certain month
      // the user should see the selected month even if there are no dates available
      // if getPlanAheadPricing is used here the call would repeat for up to 9 calls looking for available dates
      const { disabledDates, planningInfo } = await getPlanAheadFromEvent(
        dashed(monthStartDate),
        fromEvent,
        minimumAvailability,
        null
      )

      // DayPicker is the only component that uses disabledDays, it needs dates in datetime format
      const dateTimeDisabledDates = desktopDisabledDates(disabledDates)
      setPlanningInfo(planningInfo)
      setCalendarStartDate(toLuxonDate(monthStartDate))
      setDisabledDates(dateTimeDisabledDates)
      setLoading(false)
    } else {
      fetchPlanAhead(monthStartDate, null, null, null)
    }
  }

  const desktopDisabledDates = disabledDates => {
    if (disabledDates.length > 0) {
      return disabledDates.map(dashedDate => {
        return DateTime.fromISO(dashedDate).toJSDate()
      })
    }
  }

  const screenReader = e => {
    let currentDay = e.target
    if (
      currentDay.classList.value.includes('day') &&
      !currentDay.classList.value.includes('disabled')
    ) {
      currentDay.removeAttribute('aria-label')
    }
  }

  const fetchPlanAhead = async (
    startDate,
    productId = null,
    minimumAvailability,
    endDate = null
  ) => {
    try {
      const {
        disabledDates,
        firstAvailableDate,
        noDatesAvailable,
        planningInfo
      } = await getPlanAheadPricing(
        startDate,
        productId,
        minimumAvailability,
        endDate
      )

      // if there are dates available
      if (!noDatesAvailable) {
        let firstDate = DateTime.fromISO(firstAvailableDate)

        if (!startDate.isLuxonDateTime) {
          startDate = DateTime.fromISO(dashed(startDate))
        }

        let navDate = startDate.ts < firstDate.ts ? firstDate : startDate

        const dateTimeDisabledDates = desktopDisabledDates(disabledDates)
        setPlanningInfo(planningInfo)
        setCalendarStartDate(navDate)
        setDisabledDates(dateTimeDisabledDates)
      }
      setNoDatesAvailable(noDatesAvailable)
      setLoading(false)
    } catch (_error) {
      selectDay(null)
      storage.clear()
      setError(true)
    }
  }

  let mediaQuery = window.matchMedia('(max-width: 44.9375em)')
  mediaMatcher(mediaQuery)

  useEffect(() => {
    setLoading(true)

    let startDate = todayDateTime
    if (TEST_JS_DATE) {
      startDate = DateTime.fromJSDate(TEST_JS_DATE)
    } else if (queryDate) {
      startDate = DateTime.fromJSDate(new Date(queryDate))
    }

    let useStorageDateTime = null
    if (storageDateTime) {
      setSelectedDay(storageDateTime)

      if (storageDateTime?.month !== startDate?.month) {
        // keep useStorageDateTime as null if they are in the same month
        // useStorageDateTime is for desktop view
        useStorageDateTime = storageDateTime?.startOf('month')
      }
    } else {
      setSelectedDay(null)
    }

    !isMobiDisplayed &&
      fetchPlanAhead(
        useStorageDateTime || startDate,
        fromEvent,
        minimumAvailability
      )
  }, [isMobiDisplayed])

  const renderLoading = () => {
    if (!loading) {
      return null
    }
    if (error) {
      if (fromEvent) {
        return <div></div>
      } else {
        return (
          <div className="text-16 font-bold">
            Oops! Something went wrong. Please refresh this page and we'll try
            again.
          </div>
        )
      }
    }
    return <Loading text="One moment while we load the calendar." />
  }

  const renderSelectionAnnouncement = () => {
    // hiding this when isMobiDisplayed === true because the mobile calendar doesn't select a date automatically
    // so it would read the incorrect date on load for mobile view
    if (!selectedDay) {
      return null
    }

    return (
      <span
        className="sr-only"
        role="status"
        tabIndex="-1"
      >
        You have selected{' '}
        <time dateTime={dashed(selectedDay)}>{fullDay(selectedDay)}</time>
      </span>
    )
  }

  const renderActiveMonthAnnouncement = () => {
    if (activeMonth == null) {
      return null
    }

    return (
      <p
        className="sr-only"
        role="status"
        tabIndex="-1"
      >
        {monthYear(activeMonth)}
      </p>
    )
  }

  const renderDay = day => {
    const date = day.getDate()
    const disabledDate =
      Array.isArray(disabledDates) &&
      disabledDates?.find(
        disabledDate => disabledDate.toDateString() === day.toDateString()
      )
    const availableDate = planningInfo.find(
      availableDate => availableDate['date'] === day.toISOString().slice(0, 10)
    )
    const projectedAttendance = availableDate?.['projectedAttendance']

    // bestPrice comes back as a string, try and parse it as a float
    const bestPriceData = parseFloat(availableDate?.['bestPrice'])

    // if we are unable to parse bestPrice as a number, set it to null otherwise use the value
    const bestPrice = isNaN(bestPriceData) ? null : bestPriceData

    // Used to set data-tickets-available attribute.  tickets are available if the date is available and the best price is a number
    const areTicketsAvailable = availableDate && bestPrice !== null
    const bestPriceDisplay =
      chosenEvent == null ? 'adults_from' : chosenEvent.eventCalendarPriceText

    const bestPriceValue = (disabledDate, bestPrice) => {
      if (disabledDate) {
        // If the date is in the disabled dates array, return an empty div
        return <div></div>
      } else if (bestPriceDisplay == 'hide') {
        return <div></div>
      } else if (bestPrice === null) {
        //  bestPrice is null display the admission unavailable messaging
        return (
          <div
            id={`${date}-best-price`}
            aria-label="general admission unavailable"
          >
            General Admission Unavailable
          </div>
        )
      } else if (bestPrice >= 0) {
        return (
          <div
            id={`${date}-best-price`}
            aria-label={`adults from ${Math.trunc(bestPrice)} ${
              bestPrice % 1 !== 0
                ? bestPrice.toFixed(2).toString().slice(-2)
                : 'dollars'
            }`}
          >
            {bestPriceDisplay == 'adults_from' && <span>Adults from </span>}
            {bestPriceDisplay == 'tickets_from' && <span>Tickets from </span>}
            <div>${bestPrice % 1 !== 0 ? bestPrice.toFixed(2) : bestPrice}</div>
          </div>
        )
      }
    }
    return (
      <div
        data-tickets-available={areTicketsAvailable ? true : false}
        tabIndex="-1"
        id={`${date}-div`}
        className={
          attendanceInfo(projectedAttendance) === 'low'
            ? null
            : `${attendanceInfo(projectedAttendance)}-attendance`
        }
        aria-label={`${day.toDateString()}`}
        aria-labelledby={`${date}-date ${date}-attendance ${date}-best-price`}
      >
        <div
          id={`${date}-attendance`}
          aria-label={`expect ${attendanceInfo(
            projectedAttendance
          )} attendance`}
        >
          <div
            className="date"
            id={`${date}-date`}
            aria-label={`${day.toDateString()}.`}
          >
            {date}
          </div>
          {attendanceInfo(projectedAttendance) === 'high' && <SVG src={high} />}
          {attendanceInfo(projectedAttendance) === 'medium' && (
            <SVG src={medium} />
          )}
        </div>
        {bestPriceValue(disabledDate, bestPrice)}
      </div>
    )
  }

  return (
    <div aria-busy={loading}>
      {loading && renderLoading()}

      {noDatesAvailable && (
        <Contact
          preText="There are no available dates for a party of your size. Please adjust your party size and try again."
          postText=""
          phone=""
          email=""
        />
      )}

      <div id={styles.mobileCalendar}>
        {isMobiDisplayed && !fromEvent && (
          <ListView
            onDayClick={onDayClick}
            loadingCalendar={loading}
            setLoading={setLoading}
            calendarStartDate={calendarStartDate}
            setCalendarStartDate={setCalendarStartDate}
            planningInfo={planningInfo}
            setPlanningInfo={setPlanningInfo}
            storageDateTime={storageDateTime}
            TEST_JS_DATE={TEST_JS_DATE}
            setError={setError}
            selectDay={selectDay}
            hours={hours}
            selectedDate={selectedDate}
            priceTableInfo={priceTableInfo}
            setPriceTableInfo={setPriceTableInfo}
            isMobiDisplayed={isMobiDisplayed}
            showModal={showModal}
          />
        )}

        {isMobiDisplayed && fromEvent && (
          <ListViewFromEvent
            onDayClick={onDayClick}
            loadingCalendar={loading}
            setLoading={setLoading}
            calendarStartDate={calendarStartDate}
            fromEvent={fromEvent}
            minimumAvailability={minimumAvailability}
            TEST_JS_DATE={TEST_JS_DATE}
            storageDateTime={storageDateTime}
            setError={setError}
            selectDay={selectDay}
            hours={hours}
            selectedDate={selectedDate}
            priceTableInfo={priceTableInfo}
            setPriceTableInfo={setPriceTableInfo}
            isMobiDisplayed={isMobiDisplayed}
            showModal={showModal}
            loading={loading}
            remainingTicketsLoading={remainingTicketsLoading}
          />
        )}
      </div>

      <div className={styles.desktopCalendar}>
        {!loading && !noDatesAvailable && (
          <>
            <DayPicker
              classNames={styles}
              fromMonth={currentDate}
              initialMonth={initialMonth}
              disabledDays={disabledDates}
              selectedDays={pickedDay}
              captionElement={CalendarCaption}
              modifiers={{ disabled: disabledDates }}
              weekdaysShort={SHORT_DAYS}
              onDayClick={onDayClick}
              onMonthChange={onMonthChange}
              renderDay={renderDay}
              onFocus={screenReader}
            />
            {!fromEvent && (
              <PriceTable
                hours={hours}
                selectedDate={selectedDate}
                planningInfo={planningInfo}
                priceTableInfo={priceTableInfo}
                setPriceTableInfo={setPriceTableInfo}
                isMobiDisplayed={isMobiDisplayed}
              />
            )}
          </>
        )}
      </div>

      {renderActiveMonthAnnouncement()}
      {!isMobiDisplayed && renderSelectionAnnouncement()}
    </div>
  )
}

export default Calendar
